import { PaginateParams, useQuery } from './useQuery';
import axios from 'axios';
import { CacheId, useCache } from './useCache';
import { CacheNames } from '../types/cache.enums';
import { PrepareUpsert } from '../types/helpers';
import { Page } from '../types/page.types';
import { OrderDirection } from '../types/global.types';

type GetOneResponse = Page | null;
type GetMultiResponse = Array<Page>;
type GetMultiParams = PaginateParams & { urls?: string[]; ids?: string[] };

type PutOneParams = Partial<PrepareUpsert<Page>>;
type PutOneResponse = Page;

type ReorderPageResponse = Page;

const cacheName = CacheNames.PAGES;
const baseUrl = '/api/pages';

export function getPage(id: string): Promise<GetOneResponse> {
  if (!id) return null;

  const cacheId: CacheId = [cacheName, id];

  const { result } = useQuery<GetOneResponse>(cacheId, () =>
    axios
      .get<GetOneResponse>(baseUrl, {
        params: { id: id },
      })
      .then((result) => result.data)
  );

  return result ?? null;
}

export async function putPage(
  id: string,
  data: PutOneParams
): Promise<PutOneResponse> {
  const result = await axios.put<PutOneResponse>(
    baseUrl,
    { page: data },
    {
      params: { id: id },
    }
  );

  useCache().clearCache(cacheName);

  return result.data;
}

export async function reorderPage(
  id: string,
  orderDirection: OrderDirection,
  orderWidth: number = 1
): Promise<ReorderPageResponse> {
  const result = await axios.patch<ReorderPageResponse>(
    `${baseUrl}/reorder`,
    { orderDirection, orderWidth },
    {
      params: { id: id },
    }
  );

  useCache().clearCache(cacheName);

  return result.data;
}

export function getPages(
  params: GetMultiParams = {}
): Promise<GetMultiResponse> {
  const cacheId: CacheId = [cacheName, params];
  const mappedParams = {
    ...params,
    ...(params.urls && { urls: params.urls.join(',') }),
    ...(params.ids && { ids: params.ids.join(',') }),
  };

  const { result } = useQuery<GetMultiResponse>(cacheId, () =>
    axios
      .get<GetMultiResponse>(baseUrl, {
        params: mappedParams,
      })
      .then((result) => result.data)
  );

  return result;
}

export function reloadAllPages(params: GetMultiParams = {}) {
  useCache().clearCache(cacheName);

  return getPages(params);
}
